import { useLazyQuery, useMutation } from "@apollo/client";
import { FormControl, Grid, InputLabel, MenuItem, Select } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import PhoneField from "components/@phone-field";
import Dialog from "components/Dialog";
import { ImageUpload } from "components/ImageUpload/ImageUpload";
import { TextField } from "components/TextField";
import { isValidPhoneNumberFormat } from "config/constants";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import HTTP_STATUS_CODES from "domains/Task/types/Column";
import { CompanyUser, Profile_User } from "generated/graphql";
import { useCountryCode, useCurrentClient } from "hooks/useCurrentUser";
import { CameraIcon } from "icons";
import { parsePhoneNumber } from "libphonenumber-js";
import React from "react";
import "styled-components/macro";
import { ValidateEmail } from "utils";
import { AddClientUser } from "../graphql/mutations";
import { AllCompanyUsersByClient } from "../graphql/queries";

 export const defaultValue: CompanyUser = {
  company_user_email: "",
  company_user_phone_number: "",
  company_user_lastname: "",
  company_user_firstname: "",
  company_user_job: "",
  company_user_profil: Profile_User.Simple,
};

export interface ReceiverProps {
  open: boolean;
  handleClose: () => void;
  isUpdate?: boolean;
  user?: CompanyUser;
}

const useStyles = makeStyles(() =>
  createStyles({
    camera: {
      position: "absolute",
      height: 30,
      width: 30,
      zIndex: 4,
      borderRadius: "100%",
      background: "#399ebf",
      display: "flex",
      justifyContent: "center",
      left: 0,
    },
    icon: {
      margin: "auto",
      padding: "auto",
      cursor: "pointer",
    },
  })
);

export default function AddUserDialog({
  open,
  handleClose,
  isUpdate = false,
  user = null,
}: ReceiverProps) {
  const [formValues, setFormValues] = React.useState<CompanyUser>(defaultValue);
  const { client,  } = useCurrentClient();
  const [isFormSubmited, setFormSubmited] = React.useState(false);
  const [, setImage] = React.useState(null);
  const [img, setImg] = React.useState(null);
  const dispatchAlert = useAlertDispatch();
  const styles = useStyles();
  const ref = React.useRef(null);
  const { text } = useText();
  const countryCode = useCountryCode();
  const [addUserCompagny] = useMutation(AddClientUser);
  const [queryUsers, { data: usersData, loading: userLoading, refetch }] = useLazyQuery(AllCompanyUsersByClient);
  const handleInputChange = React.useCallback(
    (event: any) => {
      setFormValues({
        ...formValues,
        [event.target.name]: event.target.value,
      });
    },
    [formValues]
  );

  const createUser = () => {
    addUserCompagny({
      variables: {
        input: {
          companyUser: { 
            company_user_email: formValues.company_user_email,
          company_user_picture_url: user?.company_user_picture_url,
          company_user_firstname: formValues.company_user_firstname,
          company_user_lastname: formValues.company_user_lastname,
          company_user_phone_number: formValues.company_user_phone_number,
          company_user_profil: formValues.company_user_profil,
          company_user_job: formValues.company_user_job, 
        },
          clientID: client._id,
        },
      },
    })
      .then((response) => {
        const { code, error } = response.data.addCompanyUser;
        if (code === HTTP_STATUS_CODES.SUCCESS) {
          dispatchAlert({ message: "A new user is created", severity: "success" });
          refetch();
        } else {
          dispatchAlert({
            message: `A new user is not created. ${error}`,
            severity: "warning",
          });
        }
      })
      .catch(() => {
        dispatchAlert({ message: "A new user is not created", severity: "warning" });
      });
  };

  const handleConfirm = () => {
    setFormSubmited(true);
    if (!isValidPhoneNumberFormat(formValues?.company_user_phone_number)) return;

    if (ValidateEmail(formValues?.company_user_email) === false) return;
    // eslint-disable-next-line no-restricted-syntax
    for (const el in formValues)
      if (
        (el === "company_user_firstname" || el === "company_user_lastname" || el === "company_user_email") &&
        formValues[el]?.length === 0
      )
        return;
    const user = {
      ...formValues,
      company_user_job: formValues.company_user_job || undefined,
      company_user_phone_number: formValues.company_user_phone_number.length
        ? parsePhoneNumber(formValues.company_user_phone_number, countryCode)?.number?.toString()
        : undefined,
    };
    createUser();
    setFormSubmited(false);
    setFormValues(defaultValue);
    handleClose();
  };

  React.useEffect(() => {
    if (isUpdate && user != null) {
      setFormValues({
        ...user,
      });
    } else {
      setFormValues(defaultValue);
    }
  }, [user, isUpdate]);

  /**
   * Function used to create a form data object that will store user uploaded image
   * You can store the formData result in component state or uploaded directly.
   * @param data
   */
  const handleImageUpload = async (data: File) => {
    if (data) {
      setImage(data);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files[0]) {
      const { type } = files[0];
      if (type.indexOf("image") !== 0) {
        return dispatchAlert({
          message: "The image is not deleted",
          severity: "warning",
        });
      }
      setImg(URL.createObjectURL(files[0]));
      handleImageUpload(files[0]);
    }
  };

  const isValidPhone = () =>
    formValues.company_user_phone_number.length && isValidPhoneNumberFormat(formValues.company_user_phone_number);

  return (
    <Dialog
      title={isUpdate ? `${text.user.user_update}` : `${text.user.user_creation}`}
      open={open}
      confirmButtonTitle={isUpdate ? `${text.all.update}` : `${text.all.add}`}
      handleClose={() => handleClose()}
      handleConfirm={() => {
        handleConfirm();
      }}
      maxWidth="lg"
      key="add user dialog"
    >
      <Grid container spacing={4} style={{ padding: "54px 54px", position: "relative" }}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={text.all.first_name}
            name="company_user_firstname"
            value={formValues?.company_user_firstname}
            onChange={handleInputChange}
            error={isFormSubmited && formValues?.company_user_firstname?.length === 0}
            helperText={
              isFormSubmited && formValues?.company_user_firstname?.length === 0
                ? `${text.all.this_field_is_required}`
                : ""
            }
            required
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label={text.all.last_name}
            name="company_user_lastname"
            value={formValues?.company_user_lastname}
            onChange={handleInputChange}
            error={isFormSubmited && formValues?.company_user_lastname?.length === 0}
            helperText={
              isFormSubmited && formValues?.company_user_lastname?.length === 0
                ? `${text.all.this_field_is_required}`
                : ""
            }
            required
          />
        </Grid>

        <Grid item xs={6}>
          <PhoneField
            countryCode={countryCode}
            fullWidth
            label={text.all.phone_number}
            name="company_user_phone_number"
            value={formValues.company_user_phone_number}
            defaultValue={formValues?.company_user_phone_number}
            onValueChange={(value) => {
              setFormValues({
                ...formValues,
                company_user_phone_number: value,
              });
            }}
            error={isFormSubmited && !isValidPhone()}
            helperText={isFormSubmited && !isValidPhone() ? `${text.all.not_valid_format}` : ""}
            required
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            disabled={isUpdate}
            fullWidth
            label={text.all.email}
            name="company_user_email"
            type="email"
            value={formValues?.company_user_email}
            onChange={handleInputChange}
            error={
              isFormSubmited &&
              (formValues?.company_user_email?.length === 0 || ValidateEmail(formValues?.company_user_email) === false)
            }
            helperText={
              isFormSubmited &&
              (formValues?.company_user_email?.length === 0 || ValidateEmail(formValues?.company_user_email) === false)
                ? `${text.all.this_field_is_required}`
                : ""
            }
            required
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={text.user.user_role}
            name="company_user_job"
            type="text"
            value={formValues?.company_user_job}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl
            variant="outlined"
            fullWidth
            error={isFormSubmited && formValues?.company_user_profil?.length === 0}
            required
          >
            <InputLabel id="demo-simple-select-outlined-label">Profile</InputLabel>
            <Select
              labelId="demo-simple-select-outlined-label"
              id="demo-simple-select-outlined"
              value={formValues.company_user_profil}
              label="Profile"
              name="company_user_profil"
              onChange={handleInputChange}
              disabled={user?.company_user_profil === Profile_User.Simple}
            >
              <MenuItem value={Profile_User.Simple}>SIMPLE</MenuItem>
              <MenuItem value={Profile_User.Admin}>ADMIN</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item sm={1} style={{ padding: "10px 0px 100px 16px" }}>
          {user?.company_user_picture_url && isUpdate ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                position: "absolute",
                width: 145.3,
                height: 138,
                backgroundImage: `url(${img ?? user?.company_user_picture_url})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
              }}
            >
              <div className={styles.camera}>
                <div className={styles.icon} onClick={(event) => ref.current.click()}>
                  <input
                    id="img"
                    ref={ref}
                    name="uploaded_img"
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    hidden
                    onChange={handleChange}
                  />
                  <CameraIcon />
                </div>
              </div>
            </div>
          ) : (
            <ImageUpload onChange={handleImageUpload} />
          )}
        </Grid>
      </Grid>
    </Dialog>
  );
}
